import CountryContext from "../../CountryContext";
import {NotificationsContext} from "../../ui-components/Notifications";
import {FormattedMessage, useIntl} from "react-intl";
import {Fragment, useContext} from "react";
import React, {useState, useEffect} from "react";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import Input from "../../ui-components/Input";
import DocumentsSection from "./DocumentsSection";
import {CreateReportModal} from "../../feature/worksites";
import {useWorksiteData} from "../../hooks/useWorksiteData";
import {useWorksiteDocumentsAndData} from "../../hooks/useWorksiteDocumentsAndData";
import {
	getOnDocumentSelected,
	getOnCancelSelection,
	getOnSearchFilterChanged,
	getOnSelectAllSectionDocuments,
	getOnSelectAllGroupDocuments,
} from "./utils/events";
import {sectionsOrder} from "./utils/constants";
import EditGroupModal from "./EditGroupModal"
import Placeholder from "@ui-components/Placeholder";
import DeleteUserDocumentsDialog from "./DeleteUserDocumentsDialog";
import EditDocumentModal from "@pages/WorksitesDocuments/EditDocumentModal";
const WorksiteDocumentsComponent = (
	{
		worksite
	}
) => {
	// params
	// const [search, setSearch] = useState("")

  // context
  const intl = useIntl();
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);

	const countryApiRequest = useCountryAxiosInstance(country.code);

	// worksite documents and data
	const {
		documents,
		setDocuments,
		creators,
		sharing,
		servicesSurveys,
		chosenSurveys,
		showReportsSelectionWorksiteDialog,
		setShowReportsSelectionWorksiteDialog,
		reportDialogCallback,
		showEditGroup,
		setShowEditGroup,
		showEditDocument,
		setShowEditDocument,
		worksiteUsers,
		setUserLoadedDocuments,
		showDeleteUserDocumentsDialog,
		setShowDeleteUserDocumentsDialog,
		moveUserDocument,
		search,
		setSearch,
	} = useWorksiteDocumentsAndData(worksite);

	const onDocumentsSelected = getOnDocumentSelected(documents, setDocuments);
	const cancelSelection = getOnCancelSelection(documents, setDocuments);
	const onSearchFilterChanged = getOnSearchFilterChanged(documents, setDocuments);
	const onSelectAllSectionDocuments = getOnSelectAllSectionDocuments(documents, setDocuments);
	const onSelectAllGroupDocuments = getOnSelectAllGroupDocuments(documents, setDocuments);

	const worksiteInfo = {
		address: `${worksite.address} - ${worksite.zip_code} - ${worksite.city}`,
		worksite_type: intl.formatMessage({id: worksite.cod_worksite_type}),
		worksite_construction: intl.formatMessage({id: worksite.cod_worksite_construction}),
	}

	const showSection = (sectionKey) => {
		const section = documents[sectionKey];
		if(!section.groups){
			return search === "" || search === undefined; // if search is not empty don't show the section
		}
		/*
		there are no groups in the section, the section must be shown with proper placeholder
		(e.g. the user documents section shows a placeholder to create a group).
		*/
		if (section.groups?.length === 0) {
			return search === "" || search === undefined; // if search is not empty don't show the section
		}
		/*
		show a section if at least one of its groups has at least one visible document
		or if the group has no documents and the search is empty (if the search is not empty it means that the user is
		searching for something and the section should not be shown if it has no documents at all)
		 */
		return section.groups?.some(group => group.documents?.some(doc => doc.visible)
			|| (group.documents?.length === 0 && (search === "" || search === undefined)))
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="grid lg:grid-cols-4 xl:grid-cols-6 gap-x-2 gap-y-2 text-base lg:text-lg mt-4 lg:mt-10">
				{Object.entries(worksiteInfo).map(([key, value], idx) => (
					<Fragment key={idx}>
						<FormattedMessage id={key}/>
						<p className={"font-semibold lg:col-span-3 xl:col-span-5"}>{value}</p>
					</Fragment>
				))}
			</div>
			<div className={"my-5 md:my-8 w-full lg:w-2/3 xl:w-1/2"}>
				<Input
					value={search}
					onChange={(e) => {
						setSearch(e.target.value)
						onSearchFilterChanged(e.target.value)
					}}
					leftIcon="MagnifyingGlassIcon"
				/>
			</div>
			<div className={"grid mb-20"}>
				{sectionsOrder.map((sectionKey, index) => {
					let content;
					if (sectionKey in documents)
						content = (
							showSection(sectionKey) ?
								<div className={"mb-20"}>
									<DocumentsSection
										worksiteId={worksite.id}
										section={documents[sectionKey]}
										sectionKey={sectionKey}
										onDocumentSelected={onDocumentsSelected}
										cancelSelection={cancelSelection}
										onSelectAllSectionDocuments={onSelectAllSectionDocuments}
										onSelectAllGroupDocuments={onSelectAllGroupDocuments}
										setShowEditGroup={setShowEditGroup}
										onMove={
											sectionKey === "users_documents"
												? moveUserDocument
												: undefined
										}
										search={search}
									/></div> : null
						)
					else
						content = <Placeholder height="h-40" marginClass="" />
					return (
						<div key={index}>
							{content}
						</div>
					)
				})}
			</div>
			{showReportsSelectionWorksiteDialog &&
				<CreateReportModal
					worksite={worksite}
					onClose={() => {
						setShowReportsSelectionWorksiteDialog(false)
					}}
					servicesSurveys={servicesSurveys}
					creators={creators}
					sharing={sharing}
					images={worksite?.["images_urls"]?.data}
					chosenSurveys={chosenSurveys}
					openInNewTab={false}
					onReportCreated={reportDialogCallback}
				/>
			}
			{
				showEditGroup &&
				<EditGroupModal
					group={showEditGroup}
					onClose={() => {
						setShowEditGroup(null)
					}}
					worksiteUsers={worksiteUsers}
					setUserLoadedDocuments={setUserLoadedDocuments}
					sharing={sharing}
				/>
			}
			{
				!!showEditDocument &&
				<EditDocumentModal
					documents={showEditDocument}
					onClose={() => {
						setShowEditDocument(null)
					}}
					worksiteUsers={worksiteUsers}
					setUserLoadedDocuments={setUserLoadedDocuments}
					sharing={sharing}
				/>
			}
			{
				showDeleteUserDocumentsDialog &&
				<DeleteUserDocumentsDialog
					worksiteId={worksite.id}
					documents={showDeleteUserDocumentsDialog}
					onClose={() => {
						setShowDeleteUserDocumentsDialog(false)
					}}
					setUserLoadedDocuments={setUserLoadedDocuments}
				/>
			}
		</div>
	);
}

export default WorksiteDocumentsComponent;